// page-notes-web/src/components/Modals/FolderModal.jsx
import { useState, useEffect } from 'react';
import { useFolders } from '../../contexts/FoldersContext';
import Icon from '../common/Icon';
import './FolderModal.css';
import './Modals.css';

const FOLDER_COLORS = [
  '#ffaa00', '#ff6b6b', '#51cf66', '#339af0', '#845ef7',
  '#f783ac', '#20c997', '#ffd43b', '#ff922b', '#868e96'
];

// Lucide icon names for folder icons
const FOLDER_ICONS = [
  'Folder', 'Briefcase', 'Home', 'Globe', 'Zap', 'Star', 'Heart', 'Flag', 'Bookmark', 'Tag',
  'Music', 'Camera', 'Video', 'Image', 'Map', 'Calendar', 'Clock', 'Settings', 'User', 'Users'
];

export default function FolderModal({ isOpen, onClose, folder = null }) {
  const { createFolder, updateFolder, folders } = useFolders();

  const [name, setName] = useState('');
  const [color, setColor] = useState(FOLDER_COLORS[0]);
  const [icon, setIcon] = useState(FOLDER_ICONS[0]);
  const [parentId, setParentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isEditing = !!folder;

  useEffect(() => {
    if (folder) {
      setName(folder.name || '');
      setColor(folder.color || FOLDER_COLORS[0]);
      // If folder.icon is not in FOLDER_ICONS (e.g. legacy emoji), it will still be used
      // and rendered via Icon fallback. 
      // Ideally we might want to map legacy emojis to Lucide icons here, but keeping it simple for now.
      setIcon(folder.icon || FOLDER_ICONS[0]);
      setParentId(folder.parentId || null);
    } else {
      setName('');
      setColor(FOLDER_COLORS[0]);
      setIcon(FOLDER_ICONS[0]);
      setParentId(null);
    }
    setError(null);
  }, [folder, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Folder name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await updateFolder(folder.id, { name: name.trim(), color, icon, parentId });
      } else {
        await createFolder({ name: name.trim(), color, icon, parentId });
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save folder');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Get available parent folders (exclude self and children if editing)
  const availableParents = folders.filter(f => {
    if (!isEditing) return true;
    if (f.id === folder.id) return false;
    // TODO: Also exclude children of current folder to prevent cycles
    return true;
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Folder' : 'Create Folder'}</h2>
          <button className="modal-close" onClick={onClose}><Icon name="X" size="sm" /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error">{error}</div>}

            {/* Preview */}
            <div className="folder-preview">
              <span className="preview-icon" style={{ color }}>
                <Icon name={icon} size="lg" />
              </span>
              <span className="preview-name">{name || 'Folder Name'}</span>
            </div>

            {/* Name */}
            <div className="form-group">
              <label htmlFor="folderName">Name</label>
              <input
                id="folderName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter folder name"
                autoFocus
              />
            </div>

            {/* Icon selector */}
            <div className="form-group">
              <label>Icon</label>
              <div className="icon-grid">
                {FOLDER_ICONS.map(i => (
                  <button
                    key={i}
                    type="button"
                    className={`icon-option ${icon === i ? 'selected' : ''}`}
                    onClick={() => setIcon(i)}
                    style={{ color: icon === i ? color : undefined }}
                    title={i}
                  >
                    <Icon name={i} size="sm" />
                  </button>
                ))}
              </div>
            </div>

            {/* Color selector */}
            <div className="form-group">
              <label>Color</label>
              <div className="color-grid">
                {FOLDER_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`color-option ${color === c ? 'selected' : ''}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>

            {/* Parent folder (optional) */}
            <div className="form-group">
              <label htmlFor="parentFolder">Parent Folder (optional)</label>
              <select
                id="parentFolder"
                value={parentId || ''}
                onChange={(e) => setParentId(e.target.value || null)}
              >
                <option value="">None (root level)</option>
                {availableParents.map(f => (
                  <option key={f.id} value={f.id}>
                    {/* Note: Select options cannot render React components. 
                        We show the Name. Icon will be missing or just raw text if we tried. 
                        We just show name here. 
                    */}
                    {f.name} (ID: {f.id.slice(0, 4)})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Folder')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}